/**
 * Heure locale d'un interlocuteur + écart avec celle de qui regarde l'écran.
 *
 * Remplace l'affichage d'un identifiant de fuseau brut (« Indian/Reunion »),
 * qui n'aide personne à décider s'il peut décrocher son téléphone maintenant.
 *
 * Deux rendus, et la différence est volontaire :
 *   • MÊME fuseau → discret (gris, petit) : il n'y a rien à surveiller.
 *   • Fuseau DIFFÉRENT → mis en exergue (texte plein, mention en gras) : c'est
 *     précisément le cas où appeler sans réfléchir réveille quelqu'un.
 *
 * L'heure se rafraîchit toute seule (30 s) : une heure figée au chargement
 * deviendrait fausse sur un onglet resté ouvert, et une heure fausse est pire
 * qu'une absence d'heure.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'

import { formatOffsetDuration, timeZoneHint } from '@/lib/timezoneDisplay'

interface Props {
    timeZone: string | null | undefined
    className?: string
    /**
     * N'afficher QUE s'il y a un décalage (retour PO 2026-08-03).
     * Sur une carte de survol, « même heure que vous » est du bruit : la ligne
     * n'apporte rien et prend la place d'une information utile. Sur une fiche
     * ou un volet de détail, au contraire, la confirmation a sa valeur.
     */
    onlyWhenDiffers?: boolean
}

export function LocalTimeHint({ timeZone, className, onlyWhenDiffers }: Props) {
    const { t, i18n } = useTranslation()
    const [now, setNow] = useState(() => new Date())

    useEffect(() => {
        const timer = setInterval(() => setNow(new Date()), 30_000)
        return () => clearInterval(timer)
    }, [])

    const hint = timeZoneHint(timeZone, i18n.language || 'fr-FR', now)
    if (!hint) return null
    // Rien à signaler et le contexte ne veut que les signalements → on s'efface.
    if (onlyWhenDiffers && !hint.differs) return null

    // Fuseau illisible (identifiant inconnu du navigateur) : on retombe sur le
    // libellé plutôt que d'afficher une heure inventée.
    if (hint.time === null) {
        return (
            <span className={`flex items-center gap-2 text-xs text-muted-foreground ${className ?? ''}`}>
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                {hint.label}
            </span>
        )
    }

    if (!hint.differs) {
        return (
            <span className={`flex items-center gap-2 text-xs text-muted-foreground ${className ?? ''}`}>
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                {t('common.localTime.same', '{{time}} sur place · même heure que vous', {
                    time: hint.time,
                })}
            </span>
        )
    }

    const ahead = (hint.offset ?? 0) > 0
    const gap = formatOffsetDuration(hint.offset ?? 0)

    return (
        <span
            className={`flex items-center gap-2 text-xs text-foreground ${className ?? ''}`}
            title={hint.label}
        >
            <Clock className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>
                {t('common.localTime.time', '{{time}} sur place', { time: hint.time })}
                {' · '}
                <strong className="font-semibold">
                    {ahead
                        ? t('common.localTime.ahead', '{{gap}} d’avance sur vous', { gap })
                        : t('common.localTime.behind', '{{gap}} de retard sur vous', { gap })}
                </strong>
            </span>
        </span>
    )
}
